import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, X, ArrowRight } from 'lucide-react'
import { useAuth } from '@store/auth/AuthProvider'

const STORAGE_KEY = 'transfer_expiring_alert_date'

interface AlertaArticulo {
  matnr: string
  arktx: string
  quedan: number
  stock: number
}

interface Alerta {
  tipo: 'facil' | 'grupo'
  vbeln: string
  gueen: string
  diasRestantes: number
  articulos: AlertaArticulo[]
}

const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('ecofarte_token')}` })

function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

export function TransferExpiringAlert() {
  const [alertas, setAlertas] = useState<Alerta[]>([])
  const [visible, setVisible] = useState(false)
  const navigate = useNavigate()
  const { user } = useAuth()

  const checkAlerts = useCallback(async () => {
    try {
      const today = getTodayStr()
      const lastShown = localStorage.getItem(STORAGE_KEY)
      if (lastShown === today) return

      const response = await fetch('/api/transfers/expiring-alerts', { headers: authHeaders() })
      if (!response.ok) return
      const data = await response.json()
      const items: Alerta[] = data.data?.alertas || []

      if (items.length > 0) {
        setAlertas(items)
        setVisible(true)
      } else {
        localStorage.setItem(STORAGE_KEY, today)
      }
    } catch {
      // silent fail
    }
  }, [])

  useEffect(() => {
    if (user) {
      checkAlerts()
    }
  }, [user, checkAlerts])

  const handleClose = useCallback(() => {
    setVisible(false)
    localStorage.setItem(STORAGE_KEY, getTodayStr())
  }, [])

  const handleNavigate = useCallback((alerta: Alerta) => {
    setVisible(false)
    localStorage.setItem(STORAGE_KEY, getTodayStr())
    const path = alerta.tipo === 'facil' ? '/transfers/facil' : '/transfers/grupo'
    navigate(`${path}?vbeln=${alerta.vbeln}`)
  }, [navigate])

  if (!visible || alertas.length === 0) return null

  const totalArticulos = alertas.reduce((sum, a) => sum + a.articulos.length, 0)

  return (
    <div className="fixed bottom-4 right-4 z-[90] animate-in slide-in-from-bottom-2 fade-in duration-300">
      <div className="flex flex-col rounded-lg border border-amber-200 bg-white shadow-lg max-w-[420px]">
        <div className="flex items-start gap-3 p-4 pb-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800">Transfers próximos a vencer</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {alertas.length} transfer{alertas.length !== 1 ? 's' : ''} con {totalArticulos} artículo{totalArticulos !== 1 ? 's' : ''} pendiente{totalArticulos !== 1 ? 's' : ''} con stock disponible.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="shrink-0 rounded-md p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 pb-3 space-y-2">
          {alertas.map((alerta) => (
            <div
              key={`${alerta.tipo}-${alerta.vbeln}`}
              className="flex items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {alerta.vbeln}
                  <span className="ml-1.5 text-xs font-normal text-gray-500">
                    ({alerta.tipo === 'facil' ? 'Fácil' : 'Grupo'})
                  </span>
                </p>
                <p className="text-xs text-gray-500">
                  Vence el {alerta.gueen} · {alerta.articulos.length} artículo{alerta.articulos.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => handleNavigate(alerta)}
                className="shrink-0 flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
              >
                Ver
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
